/** Lumina Flash Report — refactored edge-to-edge dashboard.
 *
 *  Spec (Feb 2026 refactor):
 *  • Edge-to-edge layout (w-full px-6 lg:px-12, no max-w container)
 *  • No target bars, no budget readouts, no "vs target" badges
 *  • 5-option granularity: Daily / Weekly / 4-4-5 Period / Quarterly / YTD
 *  • Location Hub top-right for multi-site (group) tokens
 *  • Inline mini bar-charts inside each KPI card showing the sub-component breakdown
 *  • Suppliers & Invoices drill-down preserved
 *  • Standard unreconciled-data disclaimer (verbatim) on every Flash Report
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAdmin } from "@/lib/auth";
import { api, fmtGBP2, fmtDateUK, fmtDayLongUK, setVatMode } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import GlobalContextBar from "@/components/GlobalContextBar";
import FlashDisclaimer from "@/components/FlashDisclaimer";
import DisclaimerBadge from "@/components/DisclaimerBadge";
import PurchasesColumn from "@/components/PurchasesColumn";
import SupplierDrawer from "@/components/SupplierDrawer";
import DateRangePicker from "@/components/DateRangePicker";
import SyncStatus from "@/components/SyncStatus";
import PnLSummary from "@/components/PnLSummary";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartLineUp, Receipt, ChartPieSlice, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ResponsiveContainer, ComposedChart, Bar, Line, YAxis, Cell, Tooltip, XAxis, ReferenceLine, ReferenceArea, LabelList } from "recharts";
import { annotateWeeks, weekGroups, WeekLine, WeekLabel } from "@/components/weeks";
import { markExtremes, ExtremeTag, ExtremeBadge, extremeCellProps } from "@/components/extremes";
import FlashConfigEditor from "@/components/FlashConfigEditor";
import { PdfErrorState } from "@/components/PdfErrorState";
import { PriceAlertsPanel, usePriceAlerts } from "@/components/PriceAlertsPanel";
import { useTooltipDismiss } from "@/lib/useTooltipDismiss";

const GRANULARITIES = ["daily", "yesterday", "weekly", "last_week", "445", "quarterly", "ytd", "monthly", "last_month", "custom"];

export default function ClientFlashReport() {
  const adminMe = useAdmin();
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resolved, setResolved] = useState(null);   // { type, info, sites }
  const view = searchParams.get("view") === "pnl" ? "pnl" : "report";
  const granularity = GRANULARITIES.includes(searchParams.get("g")) ? searchParams.get("g") : "monthly";
  const siteId = searchParams.get("site") || "consolidated";
  const customFrom = searchParams.get("from") || null;
  const customTo = searchParams.get("to") || null;
  const vat = searchParams.get("vat") === "gross" ? "gross" : "net";
  setVatMode(vat);
  const setVat = useCallback((v) => {
    setSearchParams((sp) => { const n = new URLSearchParams(sp); n.set("vat", v); return n; }, { replace: true });
  }, [setSearchParams]);

  const setView = useCallback((v) => {
    setSearchParams((sp) => { const n = new URLSearchParams(sp); n.set("view", v); return n; }, { replace: true });
  }, [setSearchParams]);
  const setGranularity = useCallback((g) => {
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp); n.set("g", g);
      if (g !== "custom") { n.delete("from"); n.delete("to"); }
      return n;
    }, { replace: true });
  }, [setSearchParams]);
  const setSiteId = useCallback((s) => {
    setSearchParams((sp) => { const n = new URLSearchParams(sp); n.set("site", s); return n; }, { replace: true });
  }, [setSearchParams]);
  const setRange = useCallback(({ granularity: g, date_from, date_to }) => {
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp);
      n.set("g", g);
      if (g === "custom") {
        if (date_from) n.set("from", date_from);
        if (date_to) n.set("to", date_to);
      } else {
        n.delete("from"); n.delete("to");
      }
      return n;
    }, { replace: true });
  }, [setSearchParams]);

  const [dash, setDash] = useState(null);

  const [purchases, setPurchases] = useState([]);
  const [insights, setInsights] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  // Day-drill modal — populated when the accountant clicks a bar on the
  // Purchases KPI chart. Filters `purchases` down to that single ISO date.
  const [dayDrill, setDayDrill] = useState(null);

  // Step 1: resolve the magic token (client vs group + sites list)
  useEffect(() => {
    api.get(`/public/token/${token}`)
      .then((r) => setResolved(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Invalid magic link"));
  }, [token]);

  // Step 2: fetch dashboard based on granularity + active site selection
  const loadDashboard = useCallback(async () => {
    if (!resolved) return;
    const params = { granularity };
    if (granularity === "custom") {
      if (!customFrom || !customTo) return;
      params.date_from = customFrom;
      params.date_to = customTo;
    }
    if (siteId && siteId !== "consolidated") {
      params.site_id = siteId;
    }
    const url = resolved.type === "group"
      ? `/public/groups/${token}/dashboard`
      : `/public/clients/${token}/dashboard`;
    const { data } = await api.get(url, { params });
    setDash(data);
    // `vat` isn't read here but the api interceptor appends it, so a toggle must refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, granularity, siteId, customFrom, customTo, token, vat]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const forceRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.post(`/public/clients/${token}/refresh`);
      await loadDashboard();
      toast.success("Refreshed from Xero");
    } catch (e) {
      // Surface Xero's actual error (e.g. "Xero session expired — please
      // reconnect this site") instead of a generic "Refresh failed".
      const detail = e?.response?.data?.detail || e?.message || "Refresh failed";
      toast.error(detail);
    } finally {
      setRefreshing(false);
    }
  }, [token, loadDashboard]);

  // Report Templates were removed — the WYSIWYG per-client config now covers
  // all customisation needs.

  // Step 4: load supplier invoices for the active window.
  useEffect(() => {
    if (!resolved || !dash) return;
    if (resolved.type === "group") { setPurchases([]); return; }
    if (!dash.date_from || !dash.date_to) return;
    // Cover the whole chart span so bar-click day drills work, not just the headline range.
    const firstBucket = dash.sub_series?.[0]?.start;
    const from = firstBucket && firstBucket < dash.date_from ? firstBucket : dash.date_from;
    const params = { invoice_type: "ACCPAY", date_from: from, date_to: dash.date_to };
    if (siteId && siteId !== "consolidated") params.site_id = siteId;
    api.get(`/public/clients/${token}/invoices`, { params })
      .then((r) => setPurchases(r.data))
      .catch(() => setPurchases([]));
  }, [resolved, dash, token, siteId]);

  // Step 5: load variance insights for the current granularity (445 / monthly / quarterly).
  useEffect(() => {
    if (!resolved) return;
    const supported = new Set(["445", "monthly", "quarterly"]);
    if (!supported.has(granularity)) { setInsights([]); return; }
    const params = { granularity };
    if (siteId && siteId !== "consolidated") params.site_id = siteId;
    api.get(`/public/clients/${token}/insights`, { params })
      .then((r) => setInsights(r.data.insights || []))
      .catch(() => setInsights([]));
  }, [resolved, granularity, siteId, token]);

  const newSuppliers = useMemo(() => new Set(dash?.new_suppliers || []), [dash?.new_suppliers]);

  // Map of supplier -> insight, for inline tooltip rendering
  const insightsBySupplier = useMemo(() => {
    const m = {};
    for (const i of insights) m[i.supplier] = i;
    return m;
  }, [insights]);

  // ---------- derived ----------
  const summary = dash?.current_period || dash?.current_month;
  const subSeries = useMemo(() => dash?.sub_series || [], [dash]);

  // Build per-KPI mini chart series
  const { netSalesSpark, purchasesSpark, gpSpark, foodSalesSpark, bevSalesSpark, foodCosSpark, bevCosSpark } = useMemo(() => {
    if (!subSeries.length) return { netSalesSpark: [], purchasesSpark: [], gpSpark: [], foodSalesSpark: [], bevSalesSpark: [], foodCosSpark: [], bevCosSpark: [] };
    const lastIdx = subSeries.length - 1;
    // Derive the ISO date for each bucket. Backends ideally return one, but
    // for daily granularity we can fall back to `date_from + i days`. Used
    // by the Purchases bar-click deep-dive to filter invoices for the day.
    const fromIso = dash?.date_from;
    const isoForIdx = (i) => {
      if (!fromIso) return null;
      const [y, m, d] = fromIso.split("-").map(Number);
      const dt = new Date(y, m - 1, d + i);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    };
    const map = (key) => subSeries.map((b, i) => ({
      label: b.label,
      value: b[key] ?? 0,
      isCurrent: i === lastIdx,
      isToday: !!b.is_today,
      date: b.date || (b.start ? null : isoForIdx(i)),
      stlw: b.stlw ? b.stlw[key] : null,
      top: b[`${key}_top`] || null,
    }));
    // Blended GP% per bucket — derived so we can plot the daily trend line
    // in the "Theoretical GP%" card even when the backend hasn't provided
    // an explicit `blended_gp` field.
    const derivedGp = subSeries.map((b, i) => {
      const sales = Number(b.net_sales ?? ((b.food_sales || 0) + (b.bev_sales || 0)));
      const purch = Number(b.purchases ?? ((b.food_cos || 0) + (b.bev_cos || 0)));
      const gp = sales > 0 ? ((sales - purch) / sales) * 100 : 0;
      return {
        label: b.label,
        value: Number(b.blended_gp ?? gp) || 0,
        isCurrent: i === lastIdx,
        isToday: !!b.is_today,
        date: b.date || (b.start ? null : isoForIdx(i)),
      };
    });
    return {
      netSalesSpark: annotateWeeks(markExtremes(map("net_sales"))),
      purchasesSpark: annotateWeeks(map("purchases")),
      gpSpark: derivedGp,
      foodSalesSpark: annotateWeeks(markExtremes(map("food_sales"))),
      bevSalesSpark: annotateWeeks(markExtremes(map("bev_sales"))),
      foodCosSpark: annotateWeeks(map("food_cos")),
      bevCosSpark: annotateWeeks(map("bev_cos")),
    };
  }, [subSeries, dash?.date_from]);

  const { foodPurchases, bevPurchases, foodSuppliers, bevSuppliers } = useMemo(() => {
    // Only include an invoice in a category if it has non-zero line items
    // matching that classification. An invoice can appear in BOTH lists if it
    // mixes food + beverage line items, but pure OTHER invoices (e.g. software,
    // rent, insurance) are excluded from both.
    const inRange = purchases.filter((p) => !dash || ((p.invoice_date || "") >= dash.date_from && (p.invoice_date || "") <= dash.date_to));
    const food = inRange.filter((p) => (p.dry_total || 0) !== 0);
    const bev = inRange.filter((p) => (p.wet_total || 0) !== 0);
    const rollup = (list, key) => {
      const m = new Map();
      for (const inv of list) {
        const sup = inv.contact_name || "Unknown";
        const amt = key === "dry" ? (inv.dry_total || 0) : (inv.wet_total || 0);
        if (!amt) continue;
        const prev = m.get(sup) || { supplier: sup, total: 0, count: 0 };
        prev.total += amt; prev.count += 1;
        m.set(sup, prev);
      }
      return Array.from(m.values()).sort((a, b) => b.total - a.total);
    };
    return {
      foodPurchases: food, bevPurchases: bev,
      foodSuppliers: rollup(food, "dry"),
      bevSuppliers: rollup(bev, "wet"),
    };
  }, [purchases, dash]);

  // ---------- render ----------
  const salesBreakdown = useMemo(() => ({ Food: foodSalesSpark, Beverage: bevSalesSpark }), [foodSalesSpark, bevSalesSpark]);
  const cosBreakdown = useMemo(() => ({ Food: foodCosSpark, Beverage: bevCosSpark }), [foodCosSpark, bevCosSpark]);
  const pnlParams = useMemo(
    () => ({ granularity, date_from: customFrom, date_to: customTo, site_id: siteId, vat }),
    [granularity, customFrom, customTo, siteId, vat],
  );
  const pickerValue = useMemo(() => ({
    granularity,
    date_from: granularity === "custom" ? customFrom : dash?.date_from,
    date_to: granularity === "custom" ? customTo : dash?.date_to,
  }), [granularity, customFrom, customTo, dash?.date_from, dash?.date_to]);

  const priceAlerts = usePriceAlerts(
    token, dash?.date_from, dash?.date_to, siteId,
    !!dash && (resolved?.type === "client" || resolved?.type === "multi_site"),
  );

  if (error) return <div className="min-h-screen flex items-center justify-center text-[#475569] p-8" data-testid="flash-error">{error}</div>;
  if (!resolved || !dash || !summary) {
    return <FlashSkeleton />;
  }

  const heading = resolved.info.name;
  const subhead = resolved.info.location || (resolved.type === "group" ? "Multi-site group" : resolved.type === "multi_site" ? `${resolved.sites.length} sites` : "");
  const totalSales = (summary.wet_sales || 0) + (summary.dry_sales || 0);
  const totalPurch = (summary.wet_purchases || 0) + (summary.dry_purchases || 0);

  // Admin = JWT present. Pencil icons only render for admins; magic-link
  // visitors (the actual client/manager) never see config affordances.
  const isAdmin = !!adminMe;
  const cfg = dash.report_config || {};
  const fcfg = cfg.flash || {};
  const rootId = dash.root_client_id;

  // STLY £ delta (passed into KPI cards when the toggle is on so the hover
  // tooltip shows £ + % variance vs same-time-last-year).
  const stlyTotalSales = ((dash.stly?.wet_sales) || 0) + ((dash.stly?.dry_sales) || 0);
  const stlyTotalPurch = ((dash.stly?.wet_purchases) || 0) + ((dash.stly?.dry_purchases) || 0);
  const showStlyTooltip = !!fcfg?.charts?.show_stly_in_tooltip;
  const showCumulative = !!fcfg?.charts?.show_cumulative;
  const showBudget = !!fcfg?.charts?.show_budget;
  const showSupplierPctOfSales = fcfg?.suppliers?.show_pct_of_sales !== false;
  const showSplitGpMoney = fcfg?.splits?.show_gp_money !== false;
  const showKpiGpMoney = fcfg?.kpi?.emphasise_gp_money !== false;

  const xeroErrors = (dash.xero_errors || []).filter((e) => {
    const msg = String(e.error || "");
    // Both rate-limit and reauth-required have dedicated banners; suppress
    // them from the generic "connection error" strip to avoid duplication.
    return !msg.includes("xero_rate_limited") && !msg.includes("xero_reauth_required");
  });
  const hasXeroError = xeroErrors.length > 0;
  const dataSourceLabel = hasXeroError
    ? "Xero error · showing 0"
    : dash.xero_rate_limited
      ? "Live · Rate-limited"
      : (dash.data_source === "xero" || resolved.info.xero_connected) ? "Live · Xero" : (dash.data_source === "cache" ? "Live · Cache" : "Demo Data");
  const dataSourceTone = hasXeroError ? "bg-rose-50 text-rose-700" : dash.xero_rate_limited ? "bg-amber-50 text-amber-800" : (dataSourceLabel.startsWith("Live") ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700");

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Sticky navigation cluster — brand, page title, site/date/status
          controls, and the unreconciled disclaimer all stay pinned to the top
          of the viewport as the accountant scrolls through long invoice /
          drill-down lists. Backdrop-blur keeps the underlying content readable
          when it scrolls beneath. */}
      {/* Desktop: whole cluster pinned. Phones: wrapper becomes `contents` so only the slim brand bar sticks. */}
      <div className="max-md:contents md:sticky md:top-0 z-40 glass-header border-b border-[#E2E8F0] print:hidden" data-testid="flash-sticky-nav">
        <header className="max-md:sticky max-md:top-0 max-md:z-40 max-md:bg-[#F5F5F5]/90 max-md:backdrop-blur-md max-md:border-b max-md:border-[#E2E8F0]">
          <div className="w-full px-4 sm:px-6 lg:px-12 h-14 md:h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0" data-testid="flash-header-brand">
              <div className="leading-tight min-w-0">
                <div className="font-display text-lg tracking-[0.02em] font-semibold">FLASH</div>
                <div className="hidden sm:block text-[11px] uppercase tracking-[0.18em] text-[#475569] font-mono mt-0.5">
                  powered by <span className="text-[#0F172A] font-medium">Prysm Financial</span>
                </div>
              </div>
              <div className="hidden lg:block border-l border-[#E2E8F0] pl-4 ml-3 max-w-md text-[12px] leading-snug text-[#475569]" data-testid="flash-header-tagline">
                See your sales, GP, labour, EBITDA and key performance indicators in one simple view.
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <SyncStatus
                syncedAt={dash.synced_at}
                cacheTtlSeconds={dash.cache_ttl_seconds}
                onRefresh={forceRefresh}
                refreshing={refreshing}
              />
              <span data-testid="data-source-badge" className={`hidden sm:inline text-[11px] uppercase tracking-wider px-2.5 py-1 rounded font-medium ${dataSourceTone}`}>{dataSourceLabel}</span>
            </div>
          </div>
        </header>

        {/* Title + controls row */}
        <div className="w-full px-4 sm:px-6 lg:px-12 pt-4 pb-3 border-t border-[#EEEEEE] max-md:border-b max-md:border-[#E2E8F0]">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
            <div className="min-w-0">
              <p className="uppercase text-[11px] tracking-[0.16em] text-[#475569] mb-1.5 font-semibold">
                {view === "pnl" ? "Profit & Loss" : "Flash Report"} · {resolved.type === "group" ? "Group · Multi-Site" : resolved.type === "multi_site" ? `${resolved.sites.length} Sites` : "Single Site"}
              </p>
              <h1 className="font-display text-[26px] sm:text-[32px] tracking-tight leading-tight sm:leading-none font-bold break-words" data-testid="flash-client-name">{heading}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="flash-controls">
            <div className="flex items-center bg-white border border-[#E2E8F0] rounded-md p-0.5" data-testid="view-toggle">
              <button
                onClick={() => setView("report")}
                data-testid="view-report"
                className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium tracking-tight rounded transition-colors ${
                  view === "report" ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-[#EEEEEE]"
                }`}
              >
                <ChartLineUp size={12} weight={view === "report" ? "fill" : "duotone"} /> Report
              </button>
              <button
                onClick={() => setView("pnl")}
                data-testid="view-pnl"
                className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium tracking-tight rounded transition-colors ${
                  view === "pnl" ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-[#EEEEEE]"
                }`}
              >
                <ChartPieSlice size={12} weight={view === "pnl" ? "fill" : "duotone"} /> P&amp;L
              </button>
            </div>

            <VatToggle value={vat} onChange={setVat} />

            <DateRangePicker
              value={pickerValue}
              onChange={setRange}
            />

            <p data-testid="active-range" className="hidden sm:inline-flex text-[11px] font-mono text-[#475569] uppercase tracking-[0.18em] bg-white border border-[#E2E8F0] rounded-md px-3 h-10 items-center">
              {fmtDateUK(dash.date_from)} → {fmtDateUK(dash.date_to)}
            </p>

            {granularity === "monthly" && <MonthProgress dateTo={dash.date_to} />}

            {resolved.sites.length > 1 && (
              <GlobalContextBar
                sites={resolved.sites}
                siteId={siteId}
                onSiteChange={setSiteId}
                granularity={null}
                onGranularityChange={() => {}}
                showLocationHub
                showGranularity={false}
              />
            )}

            {/* Compact reconciliation status pill inline — full disclaimer expands
                below the fold; this keeps the "Unreconciled" cue visible while the
                user scrolls through long tables. */}
            <div className="sm:ml-auto" data-testid="reconciliation-status-pill">
              <DisclaimerBadge compact />
            </div>
          </div>
        </div>
      </div>

      {/* Edge-to-edge main */}
      <main className="w-full px-4 sm:px-6 lg:px-12 py-5 sm:py-6 space-y-6 sm:space-y-8 print:px-0 print:py-4">
        {/* Xero error strip */}
        {hasXeroError && (
          <div data-testid="xero-error-strip" className="border-l-2 border-rose-700 bg-rose-50/50 rounded-r-md p-4 text-xs text-rose-900">
            <p className="uppercase text-[11px] tracking-[0.2em] font-medium mb-1">Xero connection error</p>
            <ul className="space-y-0.5 font-mono">
              {xeroErrors.map((e) => <li key={e.site_id}>· site {e.site_id.slice(0, 8)}…: {e.error.split("\n")[0]}</li>)}
            </ul>
            <p className="mt-2 text-rose-800">Ask the accountant to re-connect Xero from the Client management page.</p>
          </div>
        )}

        {/* Xero rate-limit banner — non-fatal; serves any cached invoices and
            tells the user the figures may be stale rather than failing the page. */}
        {dash.xero_rate_limited && (
          <div data-testid="xero-rate-limit-strip" className="border-l-2 border-amber-700 bg-amber-50/60 rounded-r-md p-3 text-xs text-amber-900 flex items-start justify-between gap-4">
            <div>
              <p className="uppercase text-[11px] tracking-[0.2em] font-medium mb-0.5">Xero rate limit reached</p>
              <p className="font-mono">Live figures may be from cache. New data will resume automatically in ~2 minutes.</p>
            </div>
          </div>
        )}

        {/* AI Narrative Summary — sits at the very top of the analytical
            surface so managers see the story before diving into tables. */}
        <NarrativeSummaryCard
          summary={summary}
          dataReady={dash.data_source !== "demo" && totalSales > 0}
          subSeries={dash.sub_series || []}
          stly={dash.stly}
          stlw={dash.stlw}
          stlm={dash.stlm}
          topSupplierDrivers={dash.top_supplier_drivers || []}
          priceAlerts={priceAlerts?.alerts || []}
        />

        {/* Xero reauth required — refresh token dead. The affected site(s)
            have already been marked disconnected server-side; direct the
            admin to reconnect from the Client Directory (Xero Connections
            was merged into that page). */}
        {dash.xero_reauth_required && (
          <div data-testid="xero-reauth-strip" className="border-l-2 border-rose-700 bg-rose-50/60 rounded-r-md p-3 text-xs text-rose-900 flex items-start justify-between gap-4">
            <div>
              <p className="uppercase text-[11px] tracking-[0.2em] font-medium mb-0.5">Xero session expired</p>
              <p className="font-mono">One or more sites need to be reconnected to Xero.</p>
            </div>
            <a
              href="/admin"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reconnect-xero-cta"
              className="shrink-0 self-center inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-mono font-medium px-3 py-1.5 rounded border border-rose-700 text-rose-800 hover:bg-rose-700 hover:text-white transition-colors"
            >
              Reconnect Xero →
            </a>
          </div>
        )}

        {/* Three top-level KPI cards with inline mini bar charts */}
        {view === "report" && (
        <>
        <section className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="flash-kpi-row">
          <KpiCard
            testid="kpi-net-sales"
            label={vat === "gross" ? "Sales (inc VAT)" : "Net Sales (ex VAT)"}
            value={fmtGBP2(totalSales)}
            subValue={totalSales > 0 && (summary.dry_sales || summary.wet_sales) ? `${((summary.dry_sales / totalSales) * 100).toFixed(0)}/${((summary.wet_sales / totalSales) * 100).toFixed(0)} food/bev` : null}
            series={netSalesSpark}
            breakdownSeries={salesBreakdown}
            breakdownDenomSeries={netSalesSpark}
            stly={dash.stly ? { value: (dash.stly.wet_sales || 0) + (dash.stly.dry_sales || 0) } : null}
            stlyUnavailable={!!dash.stly_unavailable}
            showCumulative
            showBudget={showBudget}
            stlyInTooltip={showStlyTooltip ? { value: stlyTotalSales } : null}
            accent="#0F172A"
            editor={isAdmin && (
              <FlashConfigEditor clientId={rootId} config={cfg} scope="charts" onSaved={loadDashboard} testid="edit-kpi-charts" />
            )}
          />
          <KpiCard
            testid="kpi-purchases"
            invertDelta
            label={vat === "gross" ? "Purchases (inc VAT)" : "Purchases (ex VAT)"}
            value={fmtGBP2(totalPurch)}
            subValue={totalSales > 0 ? `${((totalPurch / totalSales) * 100).toFixed(1)}% of sales` : null}
            series={purchasesSpark}
            breakdownSeries={cosBreakdown}
            breakdownDenomSeries={netSalesSpark}
            showCumulative
            showBudget={showBudget}
            stlyInTooltip={showStlyTooltip ? { value: stlyTotalPurch } : null}
            accent="#991B1B"
            onBarClick={(bucket) => bucket?.date && setDayDrill({ date: bucket.date, label: bucket.label })}
          />
          <KpiCard
            testid="kpi-theoretical-gp"
            label="Theoretical GP%"
            value={summary.blended_gp}
            isPercent
            subValue={null}
            series={null}
            accent="#059669"
            footer={showKpiGpMoney ? (
              <div data-testid="kpi-gp-money" className="mt-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#475569] font-mono">Gross Profit</p>
                <p className="font-mono-num text-[28px] font-bold tracking-tight mt-0.5" style={{ color: "#059669" }}>{fmtGBP2(totalSales - totalPurch)}</p>
              </div>
            ) : null}
            editor={isAdmin && (
              <FlashConfigEditor clientId={rootId} config={cfg} scope="kpi" onSaved={loadDashboard} testid="edit-kpi-gp" />
            )}
          />
        </section>

        {/* Wet vs Dry split (no targets/budgets) */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="flash-split-row">
          <SplitCard
            title="Food"
            subtitle="Dry · Food"
            accent="#334155"
            sales={summary.dry_sales}
            purchases={summary.dry_purchases}
            gp={summary.dry_gp}
            shareOfTotal={totalSales ? (summary.dry_sales / totalSales) * 100 : 0}
            salesSpark={foodSalesSpark}
            cosSpark={foodCosSpark}
            showGpMoney={showSplitGpMoney}
            vat={vat}
            testid="split-food"
            editor={isAdmin && (
              <FlashConfigEditor clientId={rootId} config={cfg} scope="splits" onSaved={loadDashboard} testid="edit-split-food" />
            )}
          />
          <SplitCard
            title="Beverage"
            subtitle="Wet · Drinks"
            accent="#059669"
            sales={summary.wet_sales}
            purchases={summary.wet_purchases}
            gp={summary.wet_gp}
            shareOfTotal={totalSales ? (summary.wet_sales / totalSales) * 100 : 0}
            salesSpark={bevSalesSpark}
            cosSpark={bevCosSpark}
            showGpMoney={showSplitGpMoney}
            vat={vat}
            testid="split-beverage"
            editor={isAdmin && (
              <FlashConfigEditor clientId={rootId} config={cfg} scope="splits" onSaved={loadDashboard} testid="edit-split-beverage" />
            )}
          />
        </section>

        {/* Suppliers + Invoices drill-down — for client and multi_site tokens */}
        {(resolved.type === "client" || resolved.type === "multi_site") && (
          <section data-testid="suppliers-invoices-section">
            <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
              <div className="flex items-center gap-2">
                <div>
                  <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-1">Cost of Sales · Purchases Drill-Down</p>
                  <h2 className="font-display text-[18px] sm:text-[20px] font-semibold tracking-tight">Suppliers &amp; invoices</h2>
                </div>
                {isAdmin && (
                  <FlashConfigEditor clientId={rootId} config={cfg} scope="suppliers" onSaved={loadDashboard} testid="edit-suppliers" />
                )}
              </div>
              <p className="text-[11px] font-mono text-[#94A3B8] uppercase tracking-[0.18em]">
                Window: {fmtDateUK(dash.date_from)} → {fmtDateUK(dash.date_to)}
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <PurchasesColumn
                testid="food-purchases-col"
                title="Food"
                subtitle="Dry · Food Cost of Sales"
                accent="#991B1B"
                amountKey="dry_total"
                classification="DRY"
                invoices={foodPurchases}
                allInvoices={purchases}
                suppliers={foodSuppliers}
                insightsBySupplier={insightsBySupplier}
                newSuppliers={newSuppliers}
                totalSales={showSupplierPctOfSales ? totalSales : 0}
                onSelect={setSelected}
              />
              <PurchasesColumn
                testid="beverage-purchases-col"
                title="Beverage"
                subtitle="Wet · Beverage Cost of Sales"
                accent="#991B1B"
                amountKey="wet_total"
                classification="WET"
                invoices={bevPurchases}
                allInvoices={purchases}
                suppliers={bevSuppliers}
                insightsBySupplier={insightsBySupplier}
                newSuppliers={newSuppliers}
                totalSales={showSupplierPctOfSales ? totalSales : 0}
                onSelect={setSelected}
              />
            </div>
          </section>
        )}

        {(resolved.type === "client" || resolved.type === "multi_site") && (
          <section data-testid="price-alerts-section">
            <PriceAlertsPanel
              data={priceAlerts}
              onSelectInvoice={(id) => { const inv = purchases.find((p) => p.invoice_id === id); if (inv) setSelected(inv); }}
            />
          </section>
        )}

        {/* For group tokens — consolidated supplier table from snapshot cache */}
        {resolved.type === "group" && (
          <GroupSuppliersSection token={token} dateFrom={dash.date_from} dateTo={dash.date_to} onDrillIn={setDrawer} />
        )}

        <div className="hidden print:block">
          <FlashDisclaimer />
        </div>
        </>
        )}

        {/* P&L view */}
        {view === "pnl" && (
          <PnLSummary
            token={token}
            params={pnlParams}
          />
        )}

        <p className="text-center text-[11px] text-[#94A3B8] font-mono pt-6 print:pt-3">
          FLASH · powered by Prysm Financial · {dataSourceLabel}
        </p>
      </main>

      {/* Invoice detail modal — line items + Xero PDF preview tabs */}
      <FlashInvoiceModal
        token={token}
        siteId={siteId}
        selected={selected}
        onClose={() => setSelected(null)}
      />

      <SupplierDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        token={token}
        classification={drawer?.classification}
        month={drawer?.month}
        accent={drawer?.classification === "WET" ? "#991B1B" : "#991B1B"}
      />

      <DayPurchasesModal
        drill={dayDrill}
        purchases={purchases}
        onClose={() => setDayDrill(null)}
        onSelectInvoice={(inv) => { setDayDrill(null); setSelected(inv); }}
      />
    </div>
  );
}

function MonthProgress({ dateTo }) {
  if (!dateTo) return null;
  const [y, m, d] = dateTo.slice(0, 10).split("-").map(Number);
  const total = new Date(y, m, 0).getDate();
  const pct = Math.round((d / total) * 100);
  return (
    <div
      data-testid="month-progress"
      title={`${pct}% of the month elapsed — figures are month-to-date`}
      className="bg-white border border-[#E2E8F0] rounded-md px-3 h-10 inline-flex items-center gap-2.5"
    >
      <span className="text-[12px] font-medium text-[#0F172A]" data-testid="month-progress-label">Day {d} of {total}</span>
      <span className="w-16 h-1.5 rounded-full bg-[#EEEEEE] overflow-hidden" aria-hidden>
        <span className="block h-full bg-[#0F172A]/70 rounded-full" style={{ width: `${pct}%` }} />
      </span>
      <span className="text-[11px] font-mono-num text-[#94A3B8]">{pct}%</span>
    </div>
  );
}

function VatToggle({ value, onChange }) {
  return (
    <div className="flex items-center bg-white border border-[#E2E8F0] rounded-md p-0.5" data-testid="vat-toggle">
      {[["net", "Net"], ["gross", "Gross"]].map(([k, l]) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          data-testid={`vat-toggle-${k}`}
          title={k === "net" ? "Figures excluding VAT" : "Figures including VAT"}
          className={`px-3 py-2 text-xs font-medium tracking-tight rounded transition-colors ${
            value === k ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-[#EEEEEE]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function SplitCard({ title, subtitle, accent, sales, purchases, gp, shareOfTotal, salesSpark, cosSpark, testid, showGpMoney = true, editor, vat }) {
  const gpMoney = (Number(sales) || 0) - (Number(purchases) || 0);
  const gross = vat === "gross";
  return (
    <div data-testid={testid} className="bg-white border border-[#E2E8F0] rounded-lg p-4 sm:p-6 card-fade">
      <div className="flex items-baseline justify-between flex-wrap gap-x-3 gap-y-1 mb-5 pb-3 border-b border-[#EEEEEE]">
        <div className="flex items-center gap-2">
          <div>
            <p className="uppercase text-[11px] tracking-[0.22em] font-medium" style={{ color: accent }}>{subtitle}</p>
            <h3 className="font-display text-[18px] sm:text-[20px] font-semibold tracking-tight mt-1">{title}</h3>
          </div>
          {editor}
        </div>
        <p className="text-[11px] font-mono text-[#94A3B8] uppercase tracking-[0.18em]">
          {shareOfTotal.toFixed(1)}% of total sales
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        <SplitStat label={gross ? "Sales inc VAT" : "Net Sales"} value={fmtGBP2(sales)} accent={accent} spark={salesSpark} kind="sales" testid={`${testid}-sales`} />
        <SplitStat label={gross ? "Purchases inc VAT" : "Purchases"} value={fmtGBP2(purchases)} accent="#991B1B" spark={cosSpark} kind="cos" testid={`${testid}-cos`} />
        <div className="grid grid-cols-2 gap-4 sm:block sm:space-y-4" data-testid={`${testid}-gp-stack`}>
          <SplitStat label="Theoretical GP%" value={`${(gp ?? 0).toFixed(1)}%`} accent={accent} testid={`${testid}-gp`} />
          {showGpMoney && (
            <SplitStat label="Gross Profit" value={fmtGBP2(gpMoney)} accent={accent} testid={`${testid}-gp-money`} />
          )}
        </div>
      </div>
    </div>
  );
}

function SplitStat({ label, value, accent, spark, testid, kind }) {
  const hasSpark = spark && spark.length > 0;
  let run = 0;
  const data = hasSpark ? spark.map((d) => ({ ...d, cumulative: (run += Number(d.value) || 0) })) : [];
  const todayLabel = hasSpark ? spark.find((d) => d.isToday)?.label : null;
  const hasExtremes = hasSpark && spark.some((d) => d.extreme);
  const weeks = hasSpark ? weekGroups(spark) : [];
  const tip = useTooltipDismiss();
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#475569] font-mono mb-1.5">{label}</p>
      <p className="font-mono-num text-[22px] sm:text-[24px] font-bold tracking-tight leading-none" style={{ color: accent }}>{value}</p>
      {hasSpark && (
        <div className="mt-3 -mx-1" data-testid={`${testid}-spark`} {...tip.wake}>
          <div className={weeks.length ? "h-[96px]" : "h-12"}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
              <ComposedChart data={data} barCategoryGap={2} margin={{ top: weeks.length ? 42 : hasExtremes ? 12 : 10, right: 2, bottom: 0, left: 2 }}>
                <XAxis dataKey="label" hide />
                <YAxis yAxisId="bars" hide />
                <YAxis yAxisId="cum" hide orientation="right" />
                <Tooltip
                  active={tip.active}
                  cursor={{ fill: "rgba(28,25,23,0.06)" }}
                  wrapperStyle={{ zIndex: 50, maxWidth: "min(240px, 78vw)" }}
                  content={(p) => <SplitTooltip {...p} accent={accent} kind={kind} />}
                />
                {weeks.map((w, i) => (
                  <ReferenceArea
                    key={w.week}
                    yAxisId="bars"
                    x1={w.x1}
                    x2={w.x2}
                    fill="#EEEEEE"
                    fillOpacity={i % 2 ? 0.8 : 0}
                    ifOverflow="visible"
                    label={(p) => <WeekLabel {...p} week={w.week} total={w.total} fontSize={10} />}
                  />
                ))}
                {todayLabel && (
                  <ReferenceLine
                    yAxisId="bars"
                    x={todayLabel}
                    stroke="#64748B"
                    strokeDasharray="3 3"
                    label={{ value: "Today", position: weeks.length ? "insideTopRight" : "top", fontSize: 8, fill: "#475569", fontFamily: "ui-monospace, monospace" }}
                  />
                )}
                <Bar yAxisId="bars" dataKey="value" radius={[2, 2, 0, 0]}>
                  {spark.map((d) => (
                    <Cell key={d.label} fill={accent} fillOpacity={d.isCurrent ? 1 : 0.5} {...extremeCellProps(d)} />
                  ))}
                  {hasExtremes && <LabelList dataKey="extreme" content={(p) => <ExtremeTag {...p} fontSize={8} />} />}
                </Bar>
                <Line
                  yAxisId="cum"
                  type="monotone"
                  dataKey="cumulative"
                  stroke={accent}
                  strokeWidth={1.5}
                  strokeOpacity={0.85}
                  dot={false}
                  activeDot={{ r: 2.5, fill: accent, stroke: "#fff", strokeWidth: 1 }}
                  isAnimationActive={false}
                  data-testid={`${testid}-cumulative-line`}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function SplitTooltip({ active, payload, accent, kind }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const delta = d.stlw != null ? d.value - d.stlw : null;
  const pct = d.stlw ? (delta / Math.abs(d.stlw)) * 100 : null;
  const good = kind === "cos" ? delta <= 0 : delta >= 0;
  return (
    <div className="bg-white border border-[#E2E8F0] shadow-lg rounded px-3 py-2 text-[11px] shadow-sm min-w-[190px]" data-testid="split-tooltip">
      <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#475569]">{d.date ? fmtDayLongUK(d.date) : d.label}</p>
      <ExtremeBadge extreme={d.extreme} />
      <p className="font-mono-num text-[15px] font-bold mt-0.5" style={{ color: accent }} data-testid="split-tooltip-value">{fmtGBP2(d.value)}</p>
      {delta != null && (
        <p className={`font-mono text-[11px] mt-1 ${good ? "text-emerald-700" : "text-rose-700"}`} data-testid="split-tooltip-stlw">
          {delta >= 0 ? "+" : "−"}{fmtGBP2(Math.abs(delta))}{pct != null ? ` (${delta >= 0 ? "+" : "−"}${Math.abs(pct).toFixed(1)}%)` : ""}
          <span className="text-[#94A3B8]"> vs {d.date ? "same day last week" : "prior week"}</span>
        </p>
      )}
      <div className="mt-1.5 pt-1.5 border-t border-[#EEEEEE] space-y-1">
        <WeekLine d={d} fmt={fmtGBP2} />
        {d.cumulative != null && (
          <div className="flex items-center justify-between gap-3 text-[11px]" data-testid="split-tooltip-running-total">
            <span className="text-[#475569]">Running total</span>
            <span className="font-mono-num font-semibold text-[#0F172A]">{fmtGBP2(d.cumulative)}</span>
          </div>
        )}
      </div>
      {kind === "cos" && d.top?.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-[#EEEEEE] space-y-0.5" data-testid="split-tooltip-suppliers">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#94A3B8]">Top suppliers</p>
          {d.top.slice(0, 3).map((t) => (
            <div key={t.supplier} className="flex justify-between gap-3">
              <span className="truncate max-w-[130px] text-[#0F172A]">{t.supplier}</span>
              <span className="font-mono-num text-[#991B1B]">{fmtGBP2(t.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupSuppliersSection({ token, dateFrom, dateTo, onDrillIn }) {
  const [wet, setWet] = useState([]);
  const [dry, setDry] = useState([]);
  useEffect(() => {
    const params = { date_from: dateFrom, date_to: dateTo };
    Promise.all([
      api.get(`/public/groups/${token}/suppliers`, { params: { ...params, classification: "WET" } }),
      api.get(`/public/groups/${token}/suppliers`, { params: { ...params, classification: "DRY" } }),
    ]).then(([w, d]) => { setWet(w.data); setDry(d.data); });
  }, [token, dateFrom, dateTo]);
  return (
    <section data-testid="group-suppliers-section">
      <div className="mb-6">
        <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-1">Consolidated Suppliers · Across All Sites</p>
        <h2 className="font-display text-[18px] sm:text-[20px] font-semibold tracking-tight">Suppliers &amp; spend</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <GroupSupplierTable testid="group-food-suppliers" title="Food · Dry" accent="#991B1B" rows={dry} onDrillIn={() => onDrillIn({ classification: "DRY", month: dateTo?.slice(0, 7) })} />
        <GroupSupplierTable testid="group-bev-suppliers" title="Beverage · Wet" accent="#991B1B" rows={wet} onDrillIn={() => onDrillIn({ classification: "WET", month: dateTo?.slice(0, 7) })} />
      </div>
    </section>
  );
}

function GroupSupplierTable({ title, accent, rows, testid, onDrillIn }) {
  const grand = rows.reduce((s, r) => s + r.total, 0);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md card-fade" data-testid={testid}>
      <div className="p-6 border-b border-[#EEEEEE] flex items-baseline justify-between">
        <p className="uppercase text-[11px] tracking-[0.22em] font-medium" style={{ color: accent }}>{title}</p>
        <span className="font-mono-num text-base" style={{ color: accent }}>{fmtGBP2(grand)}</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E2E8F0]">
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] px-6 h-10">Supplier</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-10">Invoices</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-10 pr-6">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center text-xs text-[#94A3B8] py-8">No supplier activity</TableCell></TableRow>
            ) : rows.slice(0, 12).map((r) => (
              <TableRow key={r.supplier} className="border-[#EEEEEE] cursor-pointer hover:bg-[#F5F5F5]" onClick={onDrillIn}>
                <TableCell className="text-xs px-6 py-2.5">{r.supplier}</TableCell>
                <TableCell className="text-xs font-mono-num text-right py-2.5">{r.count}</TableCell>
                <TableCell className="text-xs font-mono-num text-right py-2.5 pr-6" style={{ color: accent }}>{fmtGBP2(r.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


/** Small in-line invoice modal used by the Flash Report Purchases column.
 *  Same behaviour as `PnLSummary.InvoiceDetailModal` but scoped to the shape
 *  of `selected` that `PurchasesColumn` passes in (raw cached invoice object,
 *  not fetched by id). Two tabs: line items + PDF preview.
 */
function FlashInvoiceModal({ token, siteId, selected, onClose }) {
  const [tab, setTab] = useState("lines");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    if (!selected) { setTab("lines"); setPdfUrl(null); setPdfError(""); }
  }, [selected]);

  useEffect(() => {
    if (tab !== "pdf" || !selected?.invoice_id || pdfUrl || pdfLoading) return;
    setPdfLoading(true);
    setPdfError("");
    const qp = new URLSearchParams();
    if (siteId && siteId !== "consolidated") qp.set("site_id", siteId);
    api.get(`/public/clients/${token}/invoice/${selected.invoice_id}/pdf?${qp.toString()}`, {
      responseType: "blob",
    })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        setPdfUrl(url);
      })
      .catch(async (e) => {
        // Blob responses hide the JSON error body — read it back manually.
        let detail = "";
        try {
          const raw = await e?.response?.data?.text?.();
          detail = raw ? JSON.parse(raw).detail : "";
        } catch (parseErr) {
          console.error("PDF error body unreadable", parseErr);
        }
        setPdfError(detail || e?.response?.data?.detail || "load_failed");
      })
      .finally(() => setPdfLoading(false));
  }, [tab, selected, token, siteId, pdfUrl, pdfLoading]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  return (
    <Dialog open={!!selected} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md max-w-4xl" data-testid="flash-invoice-modal" aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="font-display text-2xl tracking-tight truncate">
                {selected?.contact_name || "Invoice"} {selected?.invoice_number ? `· ${selected.invoice_number}` : ""}
              </DialogTitle>
              {selected && (
                <p className="text-xs text-[#475569] font-mono mt-1">
                  {fmtDateUK(selected.invoice_date)} · {selected.type === "ACCPAY" ? "Bill" : selected.type === "ACCREC" ? "Sales Invoice" : selected.type} · {selected.status} · Total {fmtGBP2(selected.total)}
                </p>
              )}
            </div>
          </div>
          {selected && (
            <div className="flex items-center bg-[#EEEEEE] border border-[#E2E8F0] rounded-md p-0.5 w-fit mt-3" data-testid="flash-invoice-tabs">
              <button onClick={() => setTab("lines")} data-testid="flash-invoice-tab-lines"
                className={`text-[11px] px-3 py-1.5 rounded transition-colors ${tab === "lines" ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-white"}`}>
                Line items
              </button>
              <button onClick={() => setTab("pdf")} data-testid="flash-invoice-tab-pdf"
                className={`text-[11px] px-3 py-1.5 rounded transition-colors ${tab === "pdf" ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-white"}`}>
                Invoice Preview
              </button>
            </div>
          )}
        </DialogHeader>
        {selected && tab === "lines" && (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0]">
                  <TableHead className="text-xs uppercase tracking-wider text-[#475569]">Code</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#475569]">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#475569] text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selected.line_items.map((li, i) => (
                  <TableRow key={`${selected.invoice_id}-${li.account_code}-${i}`} className="border-[#EEEEEE]">
                    <TableCell className="font-mono-num text-xs">{li.account_code}</TableCell>
                    <TableCell className="text-sm">{li.description}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtGBP2(li.line_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {selected && tab === "pdf" && (
          <div className="border border-[#E2E8F0] rounded-md overflow-hidden bg-[#F5F5F5]" data-testid="flash-invoice-pdf-viewer">
            {pdfLoading && <p className="text-sm text-[#94A3B8] py-16 text-center">Fetching supplier PDF from Xero...</p>}
            {pdfError && !pdfLoading && (
              <PdfErrorState error={pdfError} testid="flash-invoice-pdf" />
            )}
            {pdfUrl && !pdfError && (
              <iframe src={pdfUrl} title="Supplier invoice PDF" className="w-full" style={{ height: "70vh" }}
                data-testid="flash-invoice-pdf-iframe" />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}




/** Day-Drill modal — surfaced when the accountant clicks a specific bar on
 *  the Purchases KPI chart. Lists every purchase invoice recorded against
 *  that date, sortable by amount, with a click-through into the standard
 *  invoice detail modal for line-items + supplier PDF preview.
 */
function DayPurchasesModal({ drill, purchases, onClose, onSelectInvoice }) {
  const rows = useMemo(() => {
    if (!drill?.date) return [];
    return purchases
      .filter((p) => (p.invoice_date || "").slice(0, 10) === drill.date)
      .sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [purchases, drill]);
  const totalSpend = rows.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md max-w-3xl" data-testid="day-drill-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Purchases · {drill?.label || drill?.date}
          </DialogTitle>
          <p className="text-xs text-[#475569] font-mono mt-1">
            {rows.length} invoice{rows.length === 1 ? "" : "s"} · Total {fmtGBP2(totalSpend)}
          </p>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-[#94A3B8]" data-testid="day-drill-empty">
            No purchase invoices recorded on this day.
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0]">
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569]">Invoice</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569]">Supplier</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right">Amount</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right pr-2">% of day</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const amt = r.total || 0;
                  const pct = totalSpend > 0 ? (amt / totalSpend) * 100 : 0;
                  return (
                    <TableRow
                      key={r.invoice_id}
                      className="border-[#EEEEEE] cursor-pointer hover:bg-[#F5F5F5]"
                      onClick={() => onSelectInvoice(r)}
                      data-testid={`day-drill-row-${r.invoice_id}`}
                    >
                      <TableCell className="font-mono-num text-xs py-2.5">{r.invoice_number || "—"}</TableCell>
                      <TableCell className="text-xs py-2.5 text-[#0F172A]">{r.contact_name}</TableCell>
                      <TableCell className="text-right font-mono-num text-xs py-2.5" style={{ color: "#991B1B" }}>{fmtGBP2(amt)}</TableCell>
                      <TableCell className="text-right font-mono-num text-xs py-2.5 pr-2 text-[#94A3B8]">{pct.toFixed(1)}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Skeleton loader shown while the dashboard payload is streaming from the
 *  backend. Mirrors the real layout (sticky nav + narrative + KPI row +
 *  supplier & P&L columns) so the visual jump when data arrives is minimal.
 */
function FlashSkeleton() {
  const shimmer = "bg-gradient-to-r from-[#EEEEEE] via-[#E2E8F0] to-[#EEEEEE] bg-[length:200%_100%] animate-skeleton";
  const Bar = ({ className = "" }) => <div className={`rounded ${shimmer} ${className}`} />;
  const Card = ({ className = "" }) => (
    <div className={`bg-white border border-[#E2E8F0] rounded-md p-6 space-y-3 ${className}`}>
      <Bar className="h-3 w-24" />
      <Bar className="h-8 w-32" />
      <Bar className="h-3 w-28" />
      <div className="pt-6 space-y-1">
        <Bar className="h-16 w-full" />
        <Bar className="h-2 w-3/4" />
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="flash-skeleton">
      <div className="sticky top-0 z-40 glass-header border-b border-[#E2E8F0]">
        <div className="w-full px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
          <div className="space-y-1.5">
            <Bar className="h-3 w-24" />
            <Bar className="h-2.5 w-40" />
          </div>
          <div className="flex items-center gap-3">
            <Bar className="h-8 w-32" />
            <Bar className="h-6 w-20" />
          </div>
        </div>
        <div className="w-full px-6 lg:px-12 pt-4 pb-3 border-t border-[#EEEEEE] space-y-3">
          <Bar className="h-6 w-64" />
          <div className="flex gap-3 flex-wrap">
            <Bar className="h-10 w-40" />
            <Bar className="h-10 w-60" />
            <Bar className="h-10 w-56" />
          </div>
        </div>
      </div>
      <main className="w-full px-6 lg:px-12 py-6 space-y-6">
        <Bar className="h-24 w-full rounded-md" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Card /><Card /><Card />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card className="h-64" />
          <Card className="h-64" />
        </div>
      </main>
    </div>
  );
}

/** Live-data-driven "Automated Narrative Summary" card. Sits at the very top
 *  of the flash report layout so managers absorb the story before scanning
 *  tables. Purely deterministic templating over the fetched payload — three
 *  bullets in the order Sales · Purchases · Margins. Sales bullet includes
 *  comparatives vs Same Time Last Week / Month / Year.
 */
function DayCallout({ d, B, testid }) {
  const delta = d.stlw != null ? d.value - d.stlw : null;
  const pct = d.stlw ? (delta / d.stlw) * 100 : null;
  const tone = delta >= 0 ? "text-emerald-700" : "text-rose-700";
  const sign = delta >= 0 ? "+" : "−";
  return (
    <span data-testid={testid}>
      <B>{d.date ? fmtDayLongUK(d.date) : d.label}</B> (<B>{fmtGBP2(d.value)}</B>
      {delta != null && (
        <>, <B tone={tone}>{sign}{fmtGBP2(Math.abs(delta))}{pct != null && ` / ${sign}${Math.abs(pct).toFixed(1)}%`}</B> vs same day last week</>
      )})
    </span>
  );
}

function NarrativeSummaryCard({ summary, dataReady, subSeries = [], stly, stlw, stlm, topSupplierDrivers = [], priceAlerts = [] }) {
  if (!summary) return null;
  const totalSales = (summary.wet_sales || 0) + (summary.dry_sales || 0);
  const totalPurch = (summary.wet_purchases || 0) + (summary.dry_purchases || 0);

  const B = ({ children, tone }) => (
    <strong className={`font-semibold ${tone || "text-[#0F172A]"}`}>{children}</strong>
  );

  if (!dataReady || totalSales <= 0) {
    return (
      <section data-testid="narrative-summary-card"
        className="rounded-md bg-[#F5F5F5] border border-[#E2E8F0] border-l-4 border-l-indigo-500 px-6 py-5 flex items-start gap-4">
        <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
          <Sparkle size={18} weight="fill" className="text-indigo-600" />
        </div>
        <div>
          <p className="uppercase text-[11px] tracking-[0.2em] text-indigo-700 font-semibold mb-1">AI Narrative</p>
          <p className="text-sm text-[#334155]" data-testid="narrative-placeholder">
            Data sync in progress. Narrative summary will generate automatically once daily POS and invoice reconciliation complete.
          </p>
        </div>
      </section>
    );
  }

  const foodSales = summary.dry_sales || 0;
  const bevSales  = summary.wet_sales || 0;
  const foodShare = (foodSales / totalSales) * 100;
  const bevShare  = (bevSales  / totalSales) * 100;
  const foodGp = summary.dry_gp != null ? summary.dry_gp
              : (foodSales > 0 ? ((foodSales - (summary.dry_purchases || 0)) / foodSales) * 100 : 0);
  const bevGp  = summary.wet_gp != null ? summary.wet_gp
              : (bevSales  > 0 ? ((bevSales  - (summary.wet_purchases || 0)) / bevSales) * 100 : 0);
  const blendedGp = summary.blended_gp != null ? summary.blended_gp
                  : (((totalSales - totalPurch) / totalSales) * 100);
  const purchPct = (totalPurch / totalSales) * 100;

  // Peak days from subSeries — top day for sales and top day for purchases.
  const topN = (key, n) => [...subSeries]
    .map((b, i) => ({ label: b.label, v: Number(b[key]) || 0, i }))
    .filter((r) => r.v > 0)
    .sort((a, b) => b.v - a.v)
    .slice(0, n)
    .map((r) => r.label);
  const salesPeaks = topN("net_sales", 2);

  // Busiest / slowest completed trading days (same rules as the chart highlights).
  const daySeries = markExtremes(subSeries.map((b) => ({
    label: b.label, date: b.date, isToday: !!b.is_today, value: Number(b.net_sales) || 0,
    stlw: b.stlw ? (b.stlw.food_sales || 0) + (b.stlw.bev_sales || 0) : null,
  })));
  const salesWeeks = weekGroups(annotateWeeks(daySeries));
  const busiest = daySeries.find((d) => d.extreme === "peak");
  const slowest = daySeries.find((d) => d.extreme === "low");
  const purchPeaks = topN("purchases", 2);

  // Volume driver: which side (Food vs Bev) is pulling the trolley?
  const salesDriver = foodShare >= bevShare
    ? { name: "Food", share: foodShare }
    : { name: "Beverage", share: bevShare };

  // Comparatives — Same Time Last Week / Month / Year net sales variance %.
  const cmpSales = (cmp) => {
    if (!cmp) return null;
    const prior = (cmp.wet_sales || 0) + (cmp.dry_sales || 0);
    if (prior <= 0) return null;
    return ((totalSales - prior) / prior) * 100;
  };
  const vsSTLW = cmpSales(stlw);
  const vsSTLM = cmpSales(stlm);
  const vsSTLY = cmpSales(stly);

  const Delta = ({ v }) => {
    if (v == null) return <span className="text-[#94A3B8]">n/a</span>;
    const tone = v >= 0 ? "text-emerald-700" : "text-rose-700";
    const sign = v >= 0 ? "+" : "";
    return <B tone={tone}>{sign}{v.toFixed(1)}%</B>;
  };

  const stronger = foodGp >= bevGp ? { name: "Food", gp: foodGp, other: "Beverage", otherGp: bevGp }
                                   : { name: "Beverage", gp: bevGp, other: "Food", otherGp: foodGp };

  const driverList = topSupplierDrivers.slice(0, 3);

  const Bullet = ({ label, testid, children }) => (
    <li className="flex gap-2" data-testid={testid}>
      <span className="text-indigo-500 select-none">•</span>
      <span><B>{label}:</B> {children}</span>
    </li>
  );

  return (
    <section data-testid="narrative-summary-card"
      className="rounded-md bg-[#F5F5F5] border border-[#E2E8F0] border-l-4 border-l-indigo-500 px-4 sm:px-6 py-4 sm:py-5">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="hidden sm:flex shrink-0 mt-0.5 h-8 w-8 rounded-full bg-indigo-100 items-center justify-center">
          <Sparkle size={18} weight="fill" className="text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="uppercase text-[11px] tracking-[0.2em] text-indigo-700 font-semibold mb-2">AI Narrative</p>
          <ul className="space-y-2 sm:space-y-1.5 text-[13px] sm:text-[14px] leading-[1.55] sm:leading-[1.5] text-[#334155] break-words">
            <Bullet label="Sales" testid="narrative-bullet-sales">
              <B>{fmtGBP2(totalSales)}</B> net turnover (<span data-testid="narrative-sales-split">Food <B>{fmtGBP2(foodSales)}</B> · Drink <B>{fmtGBP2(bevSales)}</B></span>) — <B>{salesDriver.name}</B> leading the mix at <B>{salesDriver.share.toFixed(0)}%</B>
              {!(busiest && slowest) && salesPeaks.length > 0 && <> · peak volume on <B>{salesPeaks.join(", ")}</B></>}.
              {" "}vs last week <Delta v={vsSTLW} /> · vs last month <Delta v={vsSTLM} /> · vs last year <Delta v={vsSTLY} />.
            </Bullet>
            {busiest && slowest && (
              <Bullet label="Trading days" testid="narrative-bullet-trading-days">
                Busiest <DayCallout d={busiest} B={B} testid="narrative-busiest-day" /> · slowest <DayCallout d={slowest} B={B} testid="narrative-slowest-day" />.
              </Bullet>
            )}
            {salesWeeks.length > 0 && (
              <Bullet label="Weekly sales" testid="narrative-bullet-weekly-sales">
                {salesWeeks.map((w, i) => (
                  <span key={w.week} data-testid={`narrative-week-${w.week}`}>
                    {i > 0 && " · "}Wk {w.week} <B>{fmtGBP2(w.total)}</B>
                  </span>
                ))}
                {" "}<span className="text-[#94A3B8]">(Mon–Sun, within the selected range)</span>.
              </Bullet>
            )}
            <Bullet label="Purchases" testid="narrative-bullet-purchasing">
              <B tone="text-[#991B1B]">{fmtGBP2(totalPurch)}</B> ({purchPct.toFixed(1)}% of sales; <span data-testid="narrative-cos-split">Food <B tone="text-[#991B1B]">{fmtGBP2(summary.dry_purchases || 0)}</B> · Drink <B tone="text-[#991B1B]">{fmtGBP2(summary.wet_purchases || 0)}</B></span>)
              {purchPeaks.length > 0 && <> · heaviest on <B>{purchPeaks.join(", ")}</B></>}
              {driverList.length > 0 && (
                <> · top drivers <span data-testid="narrative-top-drivers">{driverList.map((d, i) => (
                  <span key={d.supplier}>{i > 0 && ", "}<B>{d.supplier}</B> (<B tone="text-[#991B1B]">{fmtGBP2(d.total)}</B>)</span>
                ))}</span></>
              )}.
            </Bullet>
            {priceAlerts.length > 0 && (
              <Bullet label="Price rises" testid="narrative-bullet-price-rises">
                <B>{priceAlerts.length}</B> item{priceAlerts.length === 1 ? "" : "s"} up more than 5% · costliest{" "}
                {[...priceAlerts].sort((a, b) => b.extra_cost - a.extra_cost).slice(0, 3).map((a, i) => (
                  <span key={`${a.supplier}-${a.description}`} data-testid={`narrative-price-rise-${i}`}>
                    {i > 0 && ", "}<B>{a.description}</B> (<B tone="text-rose-700">+{a.pct.toFixed(0)}%</B>, <B tone="text-[#991B1B]">{fmtGBP2(a.extra_cost)}</B> extra)
                  </span>
                ))}.
              </Bullet>
            )}
            <Bullet label="Margins" testid="narrative-bullet-margin">
              Blended GP <B tone="text-emerald-700">{blendedGp.toFixed(1)}%</B> (<span data-testid="narrative-gp-money">gross profit <B tone="text-emerald-700">{fmtGBP2(totalSales - totalPurch)}</B></span>) — {stronger.name}{" "}
              <B tone="text-emerald-700">{stronger.gp.toFixed(1)}%</B> leads, {stronger.other.toLowerCase()} at{" "}
              <B tone={stronger.otherGp < 60 ? "text-rose-700" : "text-[#334155]"}>{stronger.otherGp.toFixed(1)}%</B>.
            </Bullet>
          </ul>
        </div>
      </div>
    </section>
  );
}

